import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import type { IButtonStyles } from '@fluentui/react';
import { computedFn } from 'mobx-utils/lib/computedFn';
import {
    constructKeytip,
    createAppButton,
    createAppFlyoutAnchor,
    getKeytipString,
} from 'owa-command-ribbon';
import type RibbonFormatState from 'owa-editor-ribbonplugin-store/lib/store/schema/RibbonFormatState';
import { callEditorApi } from 'owa-editor/lib/utils/callEditorApi';
import { getComposeRibbonId } from 'owa-compose-ribbon-utils/lib/utils/getComposeRibbonId';
import { isFeatureEnabled } from 'owa-feature-flags';
import Redo from 'owa-fluent-icons-svg/lib/icons/ArrowRedoRegular';
import Undo from 'owa-fluent-icons-svg/lib/icons/ArrowUndoRegular';
import loc from 'owa-localize';
import { FormatControlId } from 'owa-ribbon-ids';
import { redoText, undoText } from './formatControlStrings.locstring.json';

const createUndoRedoAppButton = (
    isUndo: boolean,
    editorId: string,
    styles: IButtonStyles | undefined,
    tabId: number | undefined,
    isDisabled: boolean
): RibbonControlProps => {
    const controlId = isUndo ? FormatControlId.Undo : FormatControlId.Redo;
    const apiName = isUndo ? 'undo' : 'redo';
    const controlIcon = isUndo ? Undo : Redo;
    const controlText = isUndo ? undoText : redoText;

    return createAppButton(
        getComposeRibbonId(controlId, editorId),
        loc(controlText),
        () => callEditorApi(editorId, apiName),
        tabId && isFeatureEnabled('mon-ribbon-keytips')
            ? constructKeytip(
                  [getKeytipString(tabId), getKeytipString(controlId)],
                  getKeytipString(controlId)
              )
            : undefined,
        controlIcon,
        undefined /* iconColor */,
        styles,
        {
            disabled: isDisabled,
        }
    );
};

export const getUndoControl = computedFn(
    (
        editorId: string,
        styles: IButtonStyles | undefined,
        tabId: number | undefined,
        isDisabled: boolean
    ): RibbonControlProps => createUndoRedoAppButton(true, editorId, styles, tabId, isDisabled)
);

const getRedoControl = computedFn(
    (
        editorId: string,
        styles: IButtonStyles | undefined,
        tabId: number | undefined,
        isDisabled: boolean
    ): RibbonControlProps => createUndoRedoAppButton(false, editorId, styles, tabId, isDisabled)
);

export const getUndoRedoControl = computedFn(
    (
        editorId: string,
        formatViewState: RibbonFormatState,
        styles: IButtonStyles | undefined,
        tabId: number | undefined
    ): RibbonControlProps => {
        const canUndo: boolean = formatViewState.canUndo ? formatViewState.canUndo : false;
        const canRedo: boolean = formatViewState.canRedo ? formatViewState.canRedo : false;
        return createAppFlyoutAnchor(
            getComposeRibbonId(FormatControlId.UndoRedo, editorId),
            loc(undoText),
            {
                sections: [
                    {
                        controls: [
                            getUndoControl(editorId, styles, tabId, !canUndo),
                            getRedoControl(editorId, styles, tabId, !canRedo),
                        ],
                    },
                ],
            },
            tabId && isFeatureEnabled('mon-ribbon-keytips')
                ? constructKeytip(
                      [getKeytipString(tabId)],
                      getKeytipString(FormatControlId.UndoRedo),
                      true
                  )
                : undefined,
            Undo,
            undefined /* iconColor */,
            styles
        );
    }
);
